import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { WMSFetch } from '../../utils/common-api-calls';
import { aeroxchangeSource, QUOTE_LINE_PROCESSED_STATUSES } from '../../pages/WMS_SMS/data';
import { useState } from 'react';

export default function CommitModal({
    isOpen,
    setIsOpen,
    currentUser,
    setShowNotification,
    setLoadingCommit,
    loadingCommit,
    setSelectedQuoteLines,
    setData,
    data,
    selectedQuoteLines,
    selectedInventory,
    aexSubmission,
    setAexSubmission,
}) {
    const [submissionError, setSubmissionError] = useState('');
    const [submitButtonText, setSubmitButtonText] = useState('Finalize Send');

    const defaultSubmitText = 'Finalize Send';
    const loadingSubmitText = 'Sending...';
    const aexVerifyText = 'Verifying Attachments...';
    const submissionErrorText =
        'An error has occured verifying attachments on the selected inventory. Please select a different one and try again.';

    const closeModal = () => {
        setIsOpen(false);
        setSubmissionError('');
        setSubmitButtonText(defaultSubmitText);
        setAexSubmission(false);
    };

    const handleCommittingAEXLines = async () => {
        try {
            setLoadingCommit(true);
            await checkInventoryAttachmentsForAEX();
            await commitQuoteLinesAndInventoryLines();
        } catch (error) {
            setLoadingCommit(false);
            setSubmissionError(submissionErrorText);
            setSubmitButtonText(defaultSubmitText);
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Verifying Attachments',
                message:
                    'An error has occured verifying attachments for the selected inventory line. Please select a different one and try again.',
            });
        }
    };

    const checkInventoryAttachmentsForAEX = async () => {
        setSubmitButtonText(aexVerifyText);
        const selectedQuoteLineData = data?.quotes?.filter(
            (quote) => selectedQuoteLines.includes(quote.quoteLineNumber) && quote.source === aeroxchangeSource
        );
        const inventoryLineData = data?.inventoryLines?.length
            ? data?.inventoryLines?.filter((inventory) => selectedInventory.includes(inventory.inventoryName))
            : [];
        const body = {
            inventoryData: inventoryLineData,
            accountNames: selectedQuoteLineData?.map((quoteLine) => quoteLine.customer),
        };
        const response = await WMSFetch('wms/sms/verify-attachments', body, currentUser);
        if (response?.error) {
            throw Error(response.Error);
        }
    };

    const commitQuoteLinesAndInventoryLines = async () => {
        try {
            setLoadingCommit(true);
            setSubmitButtonText(loadingSubmitText);

            let selectedQuoteLineData = [];
            if (aexSubmission) {
                selectedQuoteLineData = data?.quotes?.filter(
                    (quote) => selectedQuoteLines.includes(quote.quoteLineNumber) && quote.source === aeroxchangeSource
                );
            }
            if (!aexSubmission) {
                selectedQuoteLineData = data?.quotes?.filter(
                    (quote) => selectedQuoteLines.includes(quote.quoteLineNumber) && quote.source !== aeroxchangeSource
                );
            }

            const inventoryLineData = data?.inventoryLines?.length
                ? data?.inventoryLines?.filter((inventory) => selectedInventory.includes(inventory.inventoryName))
                : [];
            const secondaryInventory = data?.secondaryInventory?.length
                ? data?.secondaryInventory?.filter((secondary) => selectedInventory.includes(secondary.name))
                : [];
            const selectedInventoryLineData = [...inventoryLineData, ...secondaryInventory];

            const body = {
                quoteLines: selectedQuoteLineData,
                inventoryLines: selectedInventoryLineData.map((inv) => {
                    const tracePictures = inv.traceLinks.find((link) => link.name === 'Trace Pictures');
                    const heroPhotoTrace = tracePictures ? [tracePictures?.link] : [];
                    return {
                        ...inv,
                        photos: heroPhotoTrace,
                        traceLinks: [...inv?.traceLinks, { name: 'Trace Hero Photos', photos: heroPhotoTrace }],
                    };
                }),
                email: currentUser.authentication.userEmail,
                userName: currentUser.authentication.userName,
            };

            const response = await WMSFetch('wms/sms/commit-quotes', body, currentUser);
            if (response?.error) {
                if (response?.error === 'Pricing') {
                    setShowNotification({
                        shown: true,
                        status: 'error',
                        title: 'Error With Pricing On Inventory',
                        message:
                            'There was a problem with this submission, please back out and reprocess. If a second issue occurs please alert management.',
                    });
                    return;
                } else {
                    throw Error(response.Error);
                }
            }

            setData((prevData) => {
                if (!prevData) return prevData;
                const processedQuoteLines = selectedQuoteLineData.map((line) => line.quoteLineNumber);
                const updated = {
                    ...prevData,
                    quotes: prevData.quotes.map((quote) => {
                        if (processedQuoteLines.includes(quote.quoteLineNumber)) {
                            return {
                                ...quote,
                                currentStep: QUOTE_LINE_PROCESSED_STATUSES.REQUESTED,
                            };
                        }
                        return quote;
                    }),
                };
                return updated;
            });
            setSelectedQuoteLines([]);
            setShowNotification({
                shown: true,
                status: 'success',
                title: 'Quote Lines Have Been Processed',
                message:
                    'Quote Lines have been successfully added to the hour queue and will be emailed in the order they were processed',
            });
        } catch (error) {
            console.log(error);
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error committing quote lines',
                message:
                    'An error has occured finalizing information for the selected quotes and inventory. If the error persists please contact your manager.',
            });
        } finally {
            setLoadingCommit(false);
            closeModal();
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as='div' className='relative z-30' onClose={() => setIsOpen(false)}>
                <div className='flex fixed top-0 left-0 justify-center items-center w-full h-full'>
                    <Transition.Child
                        as={Fragment}
                        enter='ease-out duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='ease-in duration-200'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed inset-0 z-20 w-screen h-screen bg-gray-500 bg-opacity-40 transition-opacity' />
                    </Transition.Child>
                    <div className='relative flex flex-col items-end justify-between h-fit w-[500px] border rounded-md px-5 pb-5 pt-3 bg-white gap-y-3 z-30'>
                        <h1 className='w-full text-lg text-center'>
                            Are you sure you want to commit the selected Quote Lines and Inventory? This action cannot be
                            undone.
                        </h1>
                        <p className='text-center text-red-600'>{submissionError}</p>
                        <section className='flex flex-row gap-x-5 justify-evenly w-full h-fit'>
                            <button
                                className='px-2 py-1 w-full text-black bg-gray-300 rounded-md disabled:opacity-40'
                                onClick={() => closeModal()}
                                disabled={loadingCommit}
                            >
                                Cancel
                            </button>
                            <button
                                className='px-2 py-1 w-full text-white rounded-md bg-blue-primary disabled:opacity-40'
                                onClick={() => {
                                    if (aexSubmission) {
                                        handleCommittingAEXLines();
                                    }
                                    if (!aexSubmission) {
                                        commitQuoteLinesAndInventoryLines();
                                    }
                                }}
                                disabled={loadingCommit}
                            >
                                {submitButtonText}
                            </button>
                        </section>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
